import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import categoryService from "../../services/categoryService";

// Create category
export const createCategoryThunk = createAsyncThunk(
  "category/createCategory",
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await categoryService.createCategory(categoryData);
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get all categories with filters
export const getAllCategoriesThunk = createAsyncThunk(
  "category/getAllCategories",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await categoryService.getAllCategories(filters);
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get category by ID with options
export const getCategoryByIdThunk = createAsyncThunk(
  "category/getCategoryById",
  async ({ categoryId, options = {} }, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoryById(
        categoryId,
        options
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Update category by ID
export const updateCategoryByIdThunk = createAsyncThunk(
  "category/updateCategoryById",
  async ({ categoryId, categoryData }, { rejectWithValue }) => {
    try {
      const response = await categoryService.updateCategoryById(
        categoryId,
        categoryData
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete category by ID with optional reassignment
export const deleteCategoryByIdThunk = createAsyncThunk(
  "category/deleteCategoryById",
  async ({ categoryId, reassignTo = null }, { rejectWithValue }) => {
    try {
      const response = await categoryService.deleteCategoryById(
        categoryId,
        reassignTo
      );
      return { ...response, deletedId: categoryId };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Update category order
export const updateCategoryOrderThunk = createAsyncThunk(
  "category/updateCategoryOrder",
  async (categories, { rejectWithValue }) => {
    try {
      const response = await categoryService.updateCategoryOrder(categories);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Sync category statistics
export const syncCategoryStatsThunk = createAsyncThunk(
  "category/syncCategoryStats",
  async (categoryId = null, { rejectWithValue }) => {
    try {
      const response = await categoryService.syncCategoryStats(categoryId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get categories for dropdown
export const getCategoriesForDropdownThunk = createAsyncThunk(
  "category/getCategoriesForDropdown",
  async (currentCategoryId = null, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoriesForDropdown(
        currentCategoryId
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get category tree
export const getCategoryTreeThunk = createAsyncThunk(
  "category/getCategoryTree",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoryTree(filters);
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get categories by branch
export const getCategoriesByBranchThunk = createAsyncThunk(
  "category/getCategoriesByBranch",
  async ({ branchCode, storeCode = null }, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoriesByBranch(
        branchCode,
        storeCode
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== NEW PRICING THUNKS ==========

// Set pricing rule for branch
export const setCategoryPricingForBranchThunk = createAsyncThunk(
  "category/setCategoryPricingForBranch",
  async (
    {
      categoryId,
      branchCode,
      pricingRuleId,
      reason,
      applyToProducts,
      onlyWithoutRules,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await categoryService.setCategoryPricingForBranch(
        categoryId,
        {
          branchCode,
          pricingRuleId,
          reason,
          applyToProducts,
          onlyWithoutRules,
        }
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Remove pricing rule for branch
export const removeCategoryPricingForBranchThunk = createAsyncThunk(
  "category/removeCategoryPricingForBranch",
  async (
    { categoryId, branchCode, reason, removeFromProducts },
    { rejectWithValue }
  ) => {
    try {
      const response = await categoryService.removeCategoryPricingForBranch(
        categoryId,
        branchCode,
        { reason, removeFromProducts }
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get category pricing rules
export const getCategoryPricingRulesThunk = createAsyncThunk(
  "category/getCategoryPricingRules",
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoryPricingRules(
        categoryId
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get category pricing info for branch
export const getCategoryPricingInfoThunk = createAsyncThunk(
  "category/getCategoryPricingInfo",
  async ({ categoryId, branchCode }, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoryPricingInfo(
        categoryId,
        branchCode
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Bulk update products pricing
export const bulkUpdateProductsPricingThunk = createAsyncThunk(
  "category/bulkUpdateProductsPricing",
  async (
    { categoryId, branchCode, pricingRuleId, onlyWithoutRules },
    { rejectWithValue }
  ) => {
    try {
      const response = await categoryService.bulkUpdateProductsPricing(
        categoryId,
        {
          branchCode,
          pricingRuleId,
          onlyWithoutRules,
        }
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get category pricing audit
export const getCategoryPricingAuditThunk = createAsyncThunk(
  "category/getCategoryPricingAudit",
  async ({ categoryId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoryPricingAudit(
        categoryId,
        params
      );
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get all pricing audits
export const getAllPricingAuditsThunk = createAsyncThunk(
  "category/getAllPricingAudits",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await categoryService.getAllPricingAudits(params);
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Clear category error
export const clearCategoryError = createAction("category/clearCategoryError");

// Set selected category
export const setSelectedCategory = createAction("category/setSelectedCategory");

// Toggle category expansion in tree view
export const toggleCategoryExpansion = createAction(
  "category/toggleCategoryExpansion"
);

// Clear pricing audit
export const clearPricingAudit = createAction("category/clearPricingAudit");
